import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from 'sonner';
import '@/App.css';
import Login from '@/pages/Login';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Dashboard from '@/pages/Dashboard';
import JobRecap from '@/pages/JobRecap';
import JobDashboard from '@/pages/JobDashboard';
import JobsEnhanced from '@/pages/JobsEnhanced';
import Customers from '@/pages/Customers';
import Crew from '@/pages/Crew';
import Rates from '@/pages/Rates';
import Timesheets from '@/pages/Timesheets';
import TimesheetCreate from '@/pages/TimesheetCreate';
import TimesheetDetail from '@/pages/TimesheetDetail';
import Invoices from '@/pages/Invoices';
import CustomerInvoices from '@/pages/CustomerInvoices';
import MaterialPOs from '@/pages/MaterialPOs';
import ChangeOrders from '@/pages/ChangeOrders';
import CrossCharges from '@/pages/CrossCharges';
import ImportData from '@/pages/ImportData';
import AuditLog from '@/pages/AuditLog';
import Settings from '@/pages/Settings';
import Terms from '@/pages/Terms';
import SignUp from '@/pages/SignUp';
import AdminDashboard from '@/pages/AdminDashboard';
import ClientRates from '@/pages/ClientRates';
import PurchaseOrders from '@/pages/PurchaseOrders';
import Layout from '@/components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(sessionStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const payload = { email, password };
    const response = await axios.post(`${API}/auth/login`, payload);
    const { token: newToken, user: userData } = response.data;
    sessionStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    sessionStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<SignUp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Layout>
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/dashboard/recap" element={<JobRecap />} />
                    <Route path="/dashboard/job/:id" element={<JobDashboard />} />
                    <Route path="/jobs" element={<JobsEnhanced />} />
                    <Route path="/customers" element={<Customers />} />
                    <Route path="/crew" element={<Crew />} />
                    <Route path="/rates" element={<Rates />} />
                    <Route path="/client-rates" element={<ClientRates />} />
                    <Route path="/purchase-orders" element={<PurchaseOrders />} />
                    <Route path="/timesheets" element={<Timesheets />} />
                    <Route path="/timesheets/new" element={<TimesheetCreate />} />
                    <Route path="/timesheets/:id" element={<TimesheetDetail />} />
                    <Route path="/material-pos" element={<MaterialPOs />} />
                    <Route path="/change-orders" element={<ChangeOrders />} />
                    <Route path="/cross-charges" element={<CrossCharges />} />
                    <Route path="/invoices" element={<Invoices />} />
                    <Route path="/customer-invoices" element={<CustomerInvoices />} />
                    <Route path="/import" element={<ImportData />} />
                    <Route path="/audit-log" element={<AuditLog />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/admin" element={<AdminDashboard />} />
                  </Routes>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
